import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import {
  canonicalPortalPath,
  isPortalScopeRef,
  portalScopeKey,
  portalScopeOf,
} from "src/lib/PortalPaths";
import {
  type PortalScopeData,
  STALE_PORTAL_SCOPE,
  setLoadedPortalScopeKey,
} from "src/lib/ScopedLoaders";

// An organisation's pages (`/portal/org_…/…`; the route parameter is the ID after its prefix). The
// portal layout loads its data for the organisation the URL names; one the visitor can't open (or
// that doesn't exist, or a malformed ID) is not found. `org_current` redirects to the
// organisation's own address. Signed out, the `_authed` layout shows the sign-in page at this
// address instead, and the page opens there once the visitor signs in.
export const Route = createFileRoute("/_authed/portal/org_{$orgKey}")({
  loader: async ({ parentMatchPromise, location, params }): Promise<PortalScopeData> => {
    if (!isPortalScopeRef(`org_${params.orgKey}`, "organisation")) throw notFound();
    const layout = (await parentMatchPromise).loaderData;
    // Layout data loaded for another address (a navigation that overtook another) would put the
    // page in the wrong scope: navigating here again loads it for this one. (`org_current` takes
    // whichever scope is open.)
    const scopeKey = portalScopeKey(portalScopeOf(location.pathname));
    if (layout && scopeKey && layout.scopeKey !== scopeKey) {
      // Marks the layout data stale so it reloads, however its loader last ran.
      setLoadedPortalScopeKey(STALE_PORTAL_SCOPE);
      throw redirect({ href: location.href, replace: true });
    }
    if (layout && !layout.authState)
      return { orgId: `org_${params.orgKey}`, projectId: null, project: null };
    const org = layout?.organisations.find((candidate) => candidate.id === layout.selectedOrgId);
    if (!layout?.scopeFound || !org) throw notFound();
    const project = layout.projects.items.find((item) => item.id === layout.selectedProjectId);
    const canonical = canonicalPortalPath(location.pathname, { orgId: org.id, projectId: null });
    if (canonical)
      throw redirect({
        href: `${canonical}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`,
        replace: true,
      });
    return { orgId: org.id, projectId: project?.id ?? null, project: project ?? null };
  },
});
